import React, {useContext, useMemo, useState} from "react";
import {Datatable} from "../../../../shared/components/datatable/Datatable";
import {PlayerContext} from "../../../../shared/contexts/playerContext";
import {AuthContext} from "../../../../shared/contexts/authContext";
import {Tag, Button, Select, Space, Typography} from "antd";
import {getItemsNameAsNoneType} from "../../../../shared/utils/basic";
import {useItemKrTextData, useShopPackage, useShopPackageGroup} from "../../../probability/useItemData";
import IsLoading from "../../../../shared/components/IsLoading";
import ErrorPage from "../../../../shared/components/ErrorPage";
import {internalApi} from "../../../../common/axios";
import toast from "react-hot-toast";
import {getCurrentPassStatus} from "../../../../shared/utils/typeChecks";
import dayjs from "dayjs";

const {Text, Title} = Typography;

const PACKAGE_TYPE = {
    CHAIN: 1,
    PASS: 2,
    WEEKLY_PASS: 3,
    SPECIAL_PASS: 4,
    EVENT_PASS: 5,
    SHOPPING_ROAD_PASS: 6,
};

const REWARD_TYPE = {
    FREE: 1,
    PAID: 2,
    REPEAT: 3,
}

export const PassChain = () => {
    const {playerData} = useContext(PlayerContext);
    const {currentUser} = useContext(AuthContext);

    const {data: krItemTextData, isLoading: isKrItemTextDataLoading, error: krItemTextDataError} = useItemKrTextData();
    const {data: shopPackage, isLoading: isPackageLoading, error: packageError} = useShopPackage();
    const {data: shopPackageGroup, isLoading: isGroupLoading, error: groupError} = useShopPackageGroup();

    const shopData = playerData.playerInfo.shopData || {};
    const [packageType, setPackageType] = useState(PACKAGE_TYPE.CHAIN);
    const chainIndexList = Object.keys(shopData?.DicPackageDataChain?.NeedUserLevel || []);

    const passIndexList = useMemo(() => {
        const list = [];
        shopData?.BackupActivePass?.forEach((pass, idx) => list.push({value: `backup-${idx}`, label: pass.index}));
        shopData?.ActivePass?.index && list.push({value: 'active', label: shopData.ActivePass.index});
        return list;
    }, [shopData?.BackupActivePass, shopData?.ActivePass]);

    const weeklyPassIndexList = useMemo(() => {
        const list = [];
        shopData?.BackupActivePass_Weekly?.forEach((pass, idx) => list.push({
            value: `backup-${idx}`,
            label: pass.index
        }));
        shopData?.ActivePass_Weekly?.forEach((pass, idx) => list.push({value: `active-${idx}`, label: pass.index}));
        return list;
    }, [shopData?.BackupActivePass_Weekly, shopData?.ActivePass_Weekly]);

    const specialPassIndexList = useMemo(() => {
        const list = [];
        shopData?.BackupActivePass_Special?.forEach((pass, idx) => list.push({
            value: `backup-${idx}`,
            label: pass.index
        }));
        const activeSpecialIdx = shopData?.ActivePass_Special?.index;
        if (activeSpecialIdx > 0) {
            list.push({value: 'active', label: activeSpecialIdx});
        }
        return list;
    }, [shopData?.BackupActivePass_Special, shopData?.ActivePass_Special]);

    const eventPassIndexList = useMemo(() => {
        const shoppingRoadPassList = [];
        const eventPassList = [];
        const shoppingRoadData = {
            ...playerData.playerInfo?.liveEventCache?.ShoppingRoadData,
            ...playerData.playerInfo?.liveEventCache?.ShoppingRoadBackupDatas,
        };
        
        shopData?.BackupActivePass_EventPass?.forEach((pass, idx) => {
            if (Object.keys(shoppingRoadData).includes(pass.eventId.toString())) {
                shoppingRoadPassList.push({value: `backup-${idx}`, label: pass.eventId});
            } else {
                eventPassList.push({value: `backup-${idx}`, label: pass.eventId});
            }
        });
        Object.keys(shopData?.ActiveEventPass || {}).forEach((key, idx) => {
            if (Object.keys(shoppingRoadData).includes(key)) {
                shoppingRoadPassList.push({value: `active-${idx}`, label: key});
            } else {
                eventPassList.push({value: `active-${idx}`, label: key});
            }
        });
        return {eventPassList, shoppingRoadPassList};
    }, [playerData.playerInfo, shopData?.ActiveEventPass, shopData?.BackupActivePass_EventPass])

    const [isGranting, setIsGranting] = useState(false);
    const [chainIndex, setChainIndex] = useState(chainIndexList[0]);
    const [passIndex, setPassIndex] = useState(passIndexList[0]?.label);
    const [weeklyPassIndex, setWeeklyPassIndex] = useState(weeklyPassIndexList[0]?.label);
    const [specialPassIndex, setSpecialPassIndex] = useState(specialPassIndexList[0]?.label);
    const [eventPassIndex, setEventPassIndex] = useState(eventPassIndexList.eventPassList[0]?.label);
    const [shoppingRoadPassIndex, setShoppingRoadPassIndex] = useState(eventPassIndexList.shoppingRoadPassList[0]?.label);

    const getPassState = (state) => {
        const stepState = {color: 'lightgray', text: '진행 중'};
        switch (state) {
            case 1:
                stepState.color = 'blue';
                stepState.text = '완료';
                break;
            case 2:
                stepState.color = 'purple';
                stepState.text = '보상 받음';
                break;
            default:

        }
        return stepState;
    };
    const initPassData = (pass) => {
        const list = Object.values(pass.rewardNormalData).map((reward, index) => ({
            key: `${pass.index}-${index}`,
            number: index + 1,
            free: reward?.stateFree,
            paid: reward?.statePaid,
        }));

        if (pass?.rewardRepeatData) {
            list.push({
                key: `${pass.index}-loop`,
                number: '루프',
                free: pass.rewardRepeatData.stateFree,
                paid: pass.rewardRepeatData.statePaid,
            });
        }
        let missions = {};
        if (isNaN(Object.keys(pass.missionData)[0])) {
            missions = Object.keys(pass.missionData).flatMap((key) => {
                    return pass.missionData[key].map((mission) => ({
                        key: `${pass.index}-${key}-${mission.index}`,
                        type: key,
                        index: mission.index,
                        count: mission.count,
                        state: mission.state,
                    }))
                }
            );
        } else {
            missions = Object.keys(pass.missionData).flatMap((key) => {
                    return Object.values(pass.missionData).map((mission) => ({
                        key: `${pass.index}-${key}-${mission.index}`,
                        type: 'Daily',
                        index: mission.index,
                        count: mission.count,
                        state: mission.state,
                    }))
                }
            );
        }

        return {
            list,
            passPoint: pass.passPoint,
            missions,
            originalIndex: pass.index,
            purchasedProduct: pass.purchasedProduct,
            activeTime: dayjs(pass.activeTime).format('YYYY-MM-DD HH:mm:ss'),
        };
    };

    const chainData = useMemo(() => {
        if (!shopPackage || !shopPackageGroup) {
            return {};
        }
        if (packageType !== PACKAGE_TYPE.CHAIN || !shopData?.DicPackageDataChain?.NeedUserLevel) {
            return {};
        }
        const data = {};
        chainIndexList.forEach((el) => {
            const groupItems = Object.values(shopPackageGroup[currentUser.target])
                .filter((group) => group.groupIdx === shopPackage[currentUser.target][el].groupIdx);

            data[el] = groupItems.map((group, index) => ({
                key: `${el}-${index}`,
                index: el,
                number: index + 1,
                package: group.index,
                isGet: group.index < shopData.DicPackageDataChain.NeedUserLevel[el].ActivePackageGroupIndex,
            }));
        });
        return data;
    }, [packageType, chainIndexList, shopData?.DicPackageDataChain?.NeedUserLevel, shopPackage, shopPackageGroup, currentUser.target]);

    const passData = useMemo(() => {
        if (packageType !== PACKAGE_TYPE.PASS) {
            return {};
        }

        const data = {};
        shopData?.BackupActivePass?.forEach((pass, idx) => {
            data[`backup-${idx}`] = initPassData(pass);
        });
        if (shopData?.ActivePass?.activeState) {
            data['active'] = initPassData(shopData.ActivePass);
        }
        return data;
    }, [packageType, shopData?.ActivePass, shopData?.BackupActivePass]);

    const weeklyPassData = useMemo(() => {
        if (packageType !== PACKAGE_TYPE.WEEKLY_PASS) {
            return {};
        }
        const data = {};
        shopData?.BackupActivePass_Weekly?.forEach((pass, idx) => {
            data[`backup-${idx}`] = initPassData(pass);
        });
        shopData?.ActivePass_Weekly?.forEach((pass, idx) => {
            data[`active-${idx}`] = initPassData(pass);
        });
        return data;
    }, [packageType, shopData?.ActivePass_Weekly, shopData?.BackupActivePass_Weekly]);

    const specialPassData = useMemo(() => {
        if (packageType !== PACKAGE_TYPE.SPECIAL_PASS) {
            return {};
        }
        const data = {};
        shopData?.BackupActivePass_Special?.forEach((pass, idx) => {
            data[`backup-${idx}`] = initPassData(pass);
        });
        if (shopData?.ActivePass_Special?.index > 0) {
            data['active'] = initPassData(shopData.ActivePass_Special);
        }
        return data;
    }, [packageType, shopData?.ActivePass_Special, shopData?.BackupActivePass_Special]);

    const eventPassData = useMemo(() => {
        if (packageType !== PACKAGE_TYPE.EVENT_PASS && packageType !== PACKAGE_TYPE.SHOPPING_ROAD_PASS) {
            return {};
        }
        const data = {};
        shopData?.BackupActivePass_EventPass?.forEach((pass, idx) => {
            data[`backup-${idx}`] = initPassData(pass);
        })
        Object.values(shopData?.ActiveEventPass || {}).forEach((pass, idx) => {
            data[`active-${idx}`] = initPassData(pass);
        });
        return data;
    }, [packageType, shopData?.ActiveEventPass]);

    const chainColumns = useMemo(() => [
        {
            title: '체인 아이디',
            dataIndex: 'index',
        },
        {
            title: '번호',
            dataIndex: 'number',
        },
        {
            title: '패키지',
            dataIndex: 'package',
            render: (_, {package: packageIndex}) => {
                return getItemsNameAsNoneType(krItemTextData[currentUser.target], packageIndex);
            },
        },
        {
            title: '보상 받음',
            dataIndex: 'state',
            render: (_, {isGet}) => {
                const color = isGet ? 'purple' : 'blue';
                const text = isGet ? '보상받음' : '진행중';
                return <Tag color={color}>{text}</Tag>;
            },
        },
    ], [krItemTextData, currentUser.target]);

    const passColumns = useMemo(() => [
        {
            title: '단계',
            dataIndex: 'number',
            render: (number) => (isNaN(number) ? '완료 루프 보상' : `${number} 단계`),
        },
        {
            title: '무료 보상',
            dataIndex: 'free',
            render: (free) => {
                const state = getPassState(free);
                return <Tag color={state.color}>{state.text}</Tag>;
            },
        },
        {
            title: '유료 보상',
            dataIndex: 'paid',
            render: (paid) => {
                const state = getPassState(paid);
                return <Tag color={state.color}>{state.text}</Tag>;
            },
        },
    ], []);

    const passMissionColumns = useMemo(() => [
        {
            title: '타입',
            dataIndex: 'type',
        },
        {
            title: '인덱스',
            dataIndex: 'index',
        },
        {
            title: '카운트',
            dataIndex: 'count',
        },
        {
            title: '상태',
            dataIndex: 'state',
            render: (state) => {
                const stateObj = getPassState(state);
                return <Tag color={stateObj.color}>{stateObj.text}</Tag>;
            },
        }
    ], []);

    const grantUnclaimedChainRewards = async (passType, index, rewardType) => {
        try {
            await internalApi.post('player/pass/grantUnclaimedRewards', {
                userKey: playerData.userKey,
                passType,
                index,
                rewardType
            });
            toast.success('보상 지급이 성공적으로 완료되었습니다.');
        } catch (error) {
            toast.error('보상 지급에 실패했습니다.');
        }
    };

    const handleClickGrantUnclaimedRewards = async (passType, index, rewardType) => {
        if (isGranting) return;
        if (!window.confirm('정말 지급하시겠습니까?')) return;
        setIsGranting(true);
        try {
            await grantUnclaimedChainRewards(passType, index, rewardType);
        } finally {
            setIsGranting(false);
        }
    };

    if (isKrItemTextDataLoading || isPackageLoading || isGroupLoading) {
        return <IsLoading/>;
    }

    const firstError = krItemTextDataError || packageError || groupError;
    if (firstError) {
        return <ErrorPage error={firstError}/>;
    }

    const passSectionConfig = {
        [PACKAGE_TYPE.PASS]: {
            index: passIndex,
            setIndex: setPassIndex,
            indexList: passIndexList,
            data: passData[passIndex],
            passType: PACKAGE_TYPE.PASS,
        },
        [PACKAGE_TYPE.WEEKLY_PASS]: {
            index: weeklyPassIndex,
            setIndex: setWeeklyPassIndex,
            indexList: weeklyPassIndexList,
            data: weeklyPassData[weeklyPassIndex],
            passType: PACKAGE_TYPE.WEEKLY_PASS,
        },
        [PACKAGE_TYPE.SPECIAL_PASS]: {
            index: specialPassIndex,
            setIndex: setSpecialPassIndex,
            indexList: specialPassIndexList,
            data: specialPassData[specialPassIndex],
            passType: PACKAGE_TYPE.SPECIAL_PASS,
        },
        [PACKAGE_TYPE.EVENT_PASS]: {
            index: eventPassIndex,
            setIndex: setEventPassIndex,
            indexList: eventPassIndexList.eventPassList,
            data: eventPassData[eventPassIndex],
            passType: PACKAGE_TYPE.EVENT_PASS,
        },
        [PACKAGE_TYPE.SHOPPING_ROAD_PASS]: {
            index: shoppingRoadPassIndex,
            setIndex: setShoppingRoadPassIndex,
            indexList: eventPassIndexList.shoppingRoadPassList,
            data: eventPassData[shoppingRoadPassIndex],
            passType: PACKAGE_TYPE.SHOPPING_ROAD_PASS,
        }
    };

    const renderPassSection = ({index, setIndex, indexList, data, passType}) => (
        <>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}>
                <Select
                    value={index}
                    onChange={setIndex}
                    style={{width: 150}}
                    options={indexList}
                />
                <Text style={{display: 'block'}}>
                    {(() => {
                        const status = getCurrentPassStatus(data);
                        return status ? <Tag color="blue">{status}</Tag> : <Tag color="default">구매 내역 없음</Tag>;
                    })()}
                </Text>
                <Button
                    loading={isGranting}
                    onClick={() => handleClickGrantUnclaimedRewards(passType, indexList.find(el => el.value === index)?.label, REWARD_TYPE.FREE)}
                >
                    무료 보상 지급
                </Button>
                <Button
                    loading={isGranting}
                    onClick={() => handleClickGrantUnclaimedRewards(passType, indexList.find(el => el.value === index)?.label, REWARD_TYPE.PAID)}
                >
                    유료 보상 지급
                </Button>
                <Button
                    loading={isGranting}
                    onClick={() => handleClickGrantUnclaimedRewards(passType, indexList.find(el => el.value === index)?.label, REWARD_TYPE.REPEAT)}
                >
                    루프 보상 지급
                </Button>
            </div>
            <p>ActiveTime: {data?.activeTime}</p>
            <p>현재 포인트: {data?.passPoint}</p>
            <Datatable columns={passColumns} rows={data?.list || []}/>
            <Datatable columns={passMissionColumns} rows={data?.missions || []}/>
        </>
    );

    return (
        <Space direction="vertical" style={{width: '100%'}} size="large">
            <Space style={{gap: 8}}>
                <Button
                    type={packageType === PACKAGE_TYPE.CHAIN ? 'primary' : 'default'}
                    onClick={() => setPackageType(PACKAGE_TYPE.CHAIN)}
                >
                    체인 패키지
                </Button>
                <Button
                    type={packageType === PACKAGE_TYPE.PASS ? 'primary' : 'default'}
                    onClick={() => setPackageType(PACKAGE_TYPE.PASS)}
                >
                    시즌 패스
                </Button>
                <Button
                    type={packageType === PACKAGE_TYPE.WEEKLY_PASS ? 'primary' : 'default'}
                    onClick={() => setPackageType(PACKAGE_TYPE.WEEKLY_PASS)}
                >
                    주간 패스
                </Button>
                <Button
                    type={packageType === PACKAGE_TYPE.SPECIAL_PASS ? 'primary' : 'default'}
                    onClick={() => setPackageType(PACKAGE_TYPE.SPECIAL_PASS)}
                >
                    스페셜 패스
                </Button>
                <Button
                    type={packageType === PACKAGE_TYPE.EVENT_PASS ? 'primary' : 'default'}
                    onClick={() => setPackageType(PACKAGE_TYPE.EVENT_PASS)}
                >
                    이벤트 패스
                </Button>
                <Button
                    type={packageType === PACKAGE_TYPE.SHOPPING_ROAD_PASS ? 'primary' : 'default'}
                    onClick={() => setPackageType(PACKAGE_TYPE.SHOPPING_ROAD_PASS)}
                >
                    쇼핑로드 패스
                </Button>
            </Space>

            {packageType === PACKAGE_TYPE.CHAIN && (
                <>
                    <Select
                        value={chainIndex}
                        onChange={setChainIndex}
                        style={{width: 150, marginBottom: 8}}
                        options={chainIndexList.map(index => ({label: index, value: index}))}
                    />

                    <Datatable columns={chainColumns} rows={chainData[chainIndex] || []}/>
                </>
            )}

            {passSectionConfig[packageType] && renderPassSection(passSectionConfig[packageType])}
        </Space>
    );
};